/**
 * Proxy-aware fetch helpers for the Profile Activity. Mirrors client/src/api.ts: relative paths,
 * `/.proxy` prefix inside Discord, Bearer session token on every call.
 */
import type {
  NotebookNote, ProfileResponse, ProfileUpdate, VoiceUpdate,
} from '@pghbites/shared';
import { apiBase } from '../sdk/env.js';
import { authHeader } from '../auth.js';

function writeHeaders(): Record<string, string> {
  return { 'Content-Type': 'application/json', ...authHeader() };
}

/** GET the member's profile (tier + profile + preferences). */
export async function getProfile(): Promise<ProfileResponse> {
  const res = await fetch(`${apiBase()}/api/profile`, { headers: { ...authHeader() } });
  if (!res.ok) throw new Error(`Could not load profile (${res.status}).`);
  return (await res.json()) as ProfileResponse;
}

/** Upsert FREE fields; returns the fresh, server-canonical profile. */
export async function putProfile(body: ProfileUpdate): Promise<ProfileResponse> {
  const res = await fetch(`${apiBase()}/api/profile`, {
    method: 'PUT',
    headers: writeHeaders(),
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`Could not save profile (${res.status}).`);
  return (await res.json()) as ProfileResponse;
}

/** Supporter-only: save Brockett's voice dials + custom reviewer voice. Returns false on 403. */
export async function putVoice(body: VoiceUpdate): Promise<boolean> {
  const res = await fetch(`${apiBase()}/api/profile/voice`, {
    method: 'PUT',
    headers: writeHeaders(),
    body: JSON.stringify(body),
  });
  return res.ok;
}

/** Supporter-only: add a member note. Returns the new notes array (or null on failure). */
export async function addNote(text: string): Promise<NotebookNote[] | null> {
  const res = await fetch(`${apiBase()}/api/profile/notebook`, {
    method: 'PUT',
    headers: writeHeaders(),
    body: JSON.stringify({ add_note: text }),
  });
  if (!res.ok) return null;
  return ((await res.json()) as { notebook_notes?: NotebookNote[] }).notebook_notes ?? null;
}

/** Supporter-only: remove a member note. Returns the new notes array (or null on failure). */
export async function removeNote(id: string): Promise<NotebookNote[] | null> {
  const res = await fetch(`${apiBase()}/api/profile/notebook`, {
    method: 'PUT',
    headers: writeHeaders(),
    body: JSON.stringify({ remove_note_id: id }),
  });
  if (!res.ok) return null;
  return ((await res.json()) as { notebook_notes?: NotebookNote[] }).notebook_notes ?? null;
}

/** Supporter-only: forget an auto-observation by id. */
export async function removeObservation(id: string): Promise<boolean> {
  const res = await fetch(`${apiBase()}/api/profile/notebook/observation/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    headers: { ...authHeader() },
  });
  return res.ok;
}

/** Wipe everything for this member (CASCADE). */
export async function forgetMe(): Promise<boolean> {
  const res = await fetch(`${apiBase()}/api/profile/forget-me`, {
    method: 'POST',
    headers: { ...authHeader() },
  });
  return res.ok;
}
